import Moment from 'moment-timezone';
import { cloneDeep } from 'lodash';
import { i18n } from '@/plugins/i18n';
import VitalityCriterion from '@/utils/classes/VitalityCategory/VitalityCriterion';
import { defaultChartOptions, defaultDatasetProps } from '@/utils/vhub-utils';


/**
 * Score based on the evolution of the number of problems
 * NOTE: This works exactly like problems/relativeSolvedNumber.js
 * The score fluctuates as the number of reported problems changes over time.
 * If the number of problems is exactly the same overtime, score is 80.
 * If it increases when compared against the 5 previous periods, score increases and vice versa
 */
export default new VitalityCriterion({
  titleTr: 'VH_PB_REL_NUMBER',
  key: 'relativeProblemNumber',
  weight: 3,

  score: ({ problems }, range, { currentRangeIndex, ranges }) => {
    // Get the number of problems in the considered period
    // And calculate the average reported problems in the previous 5 periods
    const prevPeriodStartIdx = Math.min(currentRangeIndex + 5, ranges.length - 1);
    const prevRangeStart = ranges[prevPeriodStartIdx].start;
    const numberOfPeriods = prevPeriodStartIdx - currentRangeIndex;

    let nInPeriod = 0;
    let nInPrev = 0;
    for (let i = 0, len = problems.length; i < len; i++) {
      const pb = problems[i];
      const mDate = Moment(pb.created);
      if (mDate.isAfter(range.start) && mDate.isBefore(range.end)) {
        nInPeriod++;
      } else if (mDate.isBefore(range.start) && mDate.isAfter(prevRangeStart)) {
        nInPrev++;
      }
    }
    const averageInPrev = nInPrev / numberOfPeriods;
    let increasePerc = 0;
    if (averageInPrev === 0 && nInPeriod > 0) {
      increasePerc = 100;
    } else if (averageInPrev > 0) {
      increasePerc = (nInPeriod - averageInPrev) * 100 / averageInPrev;
    }

    let score = 0;
    if (averageInPrev > 0 || nInPeriod > 0) {
      score = increasePerc > 0
        ? 0.5 * increasePerc + 80
        : 1.4 * increasePerc + 80;
    }
    return {
      score: score.sBetweenBoundaries(0, 100),
      data: {
        value: increasePerc,
        nInPeriod,
      },
    };
  },

  text: ({ value, nInPeriod }) => {
    const nProblemsText = i18n.tc('message.N_PROBLEMS', nInPeriod, { nb: nInPeriod });
    return i18n.t('message.VH_PB_REL_NUMBER_TEXT', {
      val: `${value > 0 ? '+' : ''}${value.sToFixed(1)}`,
      nProblems: nProblemsText,
    });
  },

  chartData(teamGroups) {
    const datasets = [];
    teamGroups.forEach((group) => {
      const data = group[this.scoresKey];
      if (data) {
        const reverseData = cloneDeep(data).reverse();
        const chartData = reverseData.map((val) => {
          if (val.data && val.data.nInPeriod !== undefined) {
            return val.data.nInPeriod || 0;
          }
          return 0;
        });
        datasets.push({
          ...defaultDatasetProps,
          borderColor: group.chartColor,
          label: group.name,
          data: chartData,
          scoreData: reverseData.map(v => v.data),
        });
      }
    });
    // No need to return labels => Use ChartWrapper default 'labels'
    return { datasets };
  },

  chartOptions() {
    return defaultChartOptions(this);
  },

  chartNames() {
    return i18n.t('message.PROBLEMS');
  },
});
